import React, { useCallback, useEffect, useState } from 'react'
import { Select, Table, Tag, message } from 'antd'
import styled from 'styled-components'
import {
  listAllProductIssues,
  updateProductIssueStatus,
} from 'src/utils/productIssueApi'
import {
  PRODUCT_ISSUE_STATUS,
  getProductIssueCategoryLabel,
  getProductIssueStatusLabel,
} from 'src/utils/productIssue'

const { Option } = Select

const statusColor = {
  reported: 'orange',
  in_progress: 'blue',
  resolved: 'green',
}

/**
 * OMS-admin view of all venue product data issues.
 */
export default function AdminProductIssues() {
  const [loading, setLoading] = useState(false)
  const [view, setView] = useState('open')
  const [items, setItems] = useState([])
  const [updatingId, setUpdatingId] = useState(null)

  const load = useCallback(async () => {
    setLoading(true)
    try {
      const data = await listAllProductIssues(view === 'all' ? {} : { view })
      setItems(data?.items || [])
    } catch (error) {
      message.error(error.message || 'Could not load product issues')
      setItems([])
    } finally {
      setLoading(false)
    }
  }, [view])

  useEffect(() => {
    void load()
  }, [load])

  const handleStatusChange = async (issueId, status) => {
    setUpdatingId(issueId)
    try {
      await updateProductIssueStatus(issueId, { status })
      message.success('Status updated')
      await load()
    } catch (error) {
      message.error(error.message || 'Could not update status')
    } finally {
      setUpdatingId(null)
    }
  }

  const columns = [
    {
      title: 'Reported',
      dataIndex: 'createdAt',
      key: 'createdAt',
      render: value => (value ? new Date(value).toLocaleString('en-GB') : '—'),
      width: 160,
    },
    {
      title: 'Venue',
      dataIndex: 'venueTitle',
      key: 'venueTitle',
      ellipsis: true,
    },
    {
      title: 'Supplier',
      dataIndex: 'supplierTitle',
      key: 'supplierTitle',
      ellipsis: true,
    },
    {
      title: 'Product',
      key: 'product',
      render: (_, row) => (
        <div>
          <div>{row.productSnapshot?.description || '—'}</div>
          <Muted>{row.productSnapshot?.productCode || ''}</Muted>
        </div>
      ),
    },
    {
      title: 'Category',
      dataIndex: 'category',
      key: 'category',
      render: value => getProductIssueCategoryLabel(value),
    },
    {
      title: 'Explanation',
      dataIndex: 'explanation',
      key: 'explanation',
      ellipsis: true,
      render: value => value || '—',
    },
    {
      title: 'Routed',
      key: 'routing',
      width: 110,
      render: (_, row) => {
        if (row.routing?.sent) return <Tag color='green'>Emailed</Tag>
        if (row.routing?.attempted)
          return (
            <Tag color='default' title={row.routing?.error || ''}>
              No email
            </Tag>
          )
        return <Tag>—</Tag>
      },
    },
    {
      title: 'Status',
      key: 'status',
      width: 180,
      render: (_, row) => (
        <Select
          size='small'
          value={row.status}
          style={{ width: 150 }}
          loading={updatingId === row._id}
          onChange={status => handleStatusChange(row._id, status)}
        >
          <Option value={PRODUCT_ISSUE_STATUS.REPORTED}>
            {getProductIssueStatusLabel(PRODUCT_ISSUE_STATUS.REPORTED)}
          </Option>
          <Option value={PRODUCT_ISSUE_STATUS.IN_PROGRESS}>
            {getProductIssueStatusLabel(PRODUCT_ISSUE_STATUS.IN_PROGRESS)}
          </Option>
          <Option value={PRODUCT_ISSUE_STATUS.RESOLVED}>
            {getProductIssueStatusLabel(PRODUCT_ISSUE_STATUS.RESOLVED)}
          </Option>
        </Select>
      ),
    },
    {
      title: '',
      key: 'badge',
      width: 100,
      render: (_, row) => (
        <Tag color={statusColor[row.status] || 'default'}>
          {getProductIssueStatusLabel(row.status)}
        </Tag>
      ),
    },
  ]

  return (
    <Wrapper>
      <HeaderRow>
        <div>
          <h2 style={{ margin: 0 }}>Product Issues</h2>
          <Muted>All venues — catalogue data errors reported from OMS.</Muted>
        </div>
        <Select value={view} onChange={setView} style={{ width: 160 }}>
          <Option value='open'>Open</Option>
          <Option value='resolved'>Resolved</Option>
          <Option value='all'>All</Option>
        </Select>
      </HeaderRow>
      <Table
        rowKey={row => row._id}
        loading={loading}
        columns={columns}
        dataSource={items}
        pagination={{ pageSize: 50 }}
        scroll={{ x: 1200 }}
      />
    </Wrapper>
  )
}

const Wrapper = styled.div`
  padding: 24px;
`

const HeaderRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 16px;
  margin-bottom: 16px;
  flex-wrap: wrap;
`

const Muted = styled.div`
  color: #8c8c8c;
  font-size: 12px;
  margin-top: 4px;
`
